import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Warehouse, Truck, Train, Plane, BarChart3, Network, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { stats, services, testimonials } from '../data/mock';

export const Home = () => {
  const serviceIcons = {
    'Multi-Client Warehousing': Warehouse,
    'Dedicated Warehousing': Warehouse,
    'In-house Inventory Management': BarChart3,
    'Rail Logistics': Train,
    'Full Truck / Part Loads': Truck,
    'Air Freight': Plane,
    'HNS & ETAS Study': Network,
    'Logistics Cost Analysis': BarChart3,
    'Logistics Network Design': Network
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1761195696590-3490ea770aa1"
            alt="Modern Warehouse"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10 pt-24">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium border border-orange-500/30">
                India's Trusted Logistics Partner
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              End-to-End Supply Chain
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-orange-400">
                Simplified
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              No more juggling multiple vendors. Blue Bird combines Rail Logistics, Road Transportation, Warehousing, and Supply Chain Consulting under one powerful roof.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8 py-6 group">
                  Get Started
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 text-base px-8 py-6">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white relative -mt-20 z-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-cyan-400 mb-2 group-hover:scale-110 transition-transform">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-slate-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              India's Supply Chain Challenge
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Complex. Fragmented. Full of friction. Blue Bird was built to fix that.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Problem */}
            <Card className="border-2 border-red-200 bg-white hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <span className="text-3xl">❌</span>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">The Problem</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-slate-600">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Juggling multiple vendors</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Communication gaps</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Unexpected delays and costs</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Lack of visibility</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Solution */}
            <Card className="border-2 border-green-200 bg-white hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="text-green-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Solution</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span>One integrated partner</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span>Seamless communication</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span>Predictable, transparent pricing</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span>End-to-end ownership</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Complete Logistics Solutions
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              From warehousing to transportation to strategic consulting — we've got you covered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.slice(0, 6).map((service) => {
              const Icon = serviceIcons[service.title];
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-orange-200">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-cyan-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="text-orange-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      {service.description.substring(0, 120)}...
                    </p>
                    <Link to="/services" className="text-orange-500 font-medium text-sm hover:text-orange-600 inline-flex items-center gap-1 group">
                      Learn More
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Trusted by Leading Enterprises
            </h2>
            <p className="text-xl text-slate-600">
              See what our clients say about us
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="fill-orange-400 text-orange-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                    <p className="text-sm text-slate-500">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Supply Chain?
            </h2>
            <p className="text-xl mb-8 text-orange-50">
              Join 500+ enterprises who trust Blue Bird for their logistics needs. Get a custom quote today.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-slate-100 text-base px-8 py-6">
                Get Your Free Quote
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
